import type { SubscriptionInvoiceStatus } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Play, Receipt } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { InvoiceTable } from '@/features/subscription-billing/InvoiceTable';
import {
  billingKeys,
  platformBillingOverviewQueryOptions,
  platformInvoicesQueryOptions,
  runSubscriptionBilling,
} from '@/features/subscription-billing/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';

export const Route = createFileRoute('/_app/platform/billing')({ component: PlatformBillingPage });

const STATUSES: SubscriptionInvoiceStatus[] = ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'];

/** Tableau financier de la plateforme (ADR-023, D-H53) : MRR, encours, retards, factures, cycle. */
function PlatformBillingPage() {
  const { t, i18n } = useTranslation(['platform', 'common', 'errors']);
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = session.can('platform.read_health');
  const canManage = session.can('platform.manage_billing');
  const overview = useQuery(platformBillingOverviewQueryOptions(canRead));
  const [status, setStatus] = useState<SubscriptionInvoiceStatus | undefined>(undefined);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const filters = useMemo(
    () => ({ status, overdue: overdueOnly ? true : undefined }),
    [status, overdueOnly],
  );
  const invoices = useInfiniteQuery({ ...platformInvoicesQueryOptions(filters), enabled: canRead });
  const rows = invoices.data?.pages.flatMap((page) => page.data) ?? [];
  const date = (iso: string) => formatDateShort(iso, i18n.language);
  const money = (value: number, currency: string) => formatAmount(value, i18n.language, currency);

  const run = useMutation({
    mutationFn: () => runSubscriptionBilling(),
    onSuccess: async (report) => {
      toast.success(t('platform:billing.runDone', report));
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const kpiValue = (rowsByCurrency: { currencyCode: string; amount: number }[] | undefined) =>
    rowsByCurrency && rowsByCurrency.length > 0
      ? rowsByCurrency.map((row) => money(row.amount, row.currencyCode)).join(' · ')
      : t('platform:billing.noMoney');
  const kpiCount = (rowsByCurrency: { count: number }[] | undefined, key: string) => {
    const count = (rowsByCurrency ?? []).reduce((sum, row) => sum + row.count, 0);
    return t(`platform:billing.${key}`, { count });
  };

  return (
    <Container>
      <PageHeader
        title={t('platform:billing.title')}
        subtitle={t('platform:billing.subtitle')}
        breadcrumb={[
          { label: t('platform:title'), link: <Link to="/platform">{t('platform:title')}</Link> },
          { label: t('platform:billing.title') },
        ]}
        actions={
          canManage ? (
            <Button variant="secondary" disabled={run.isPending} onClick={() => run.mutate()}>
              <Play className="ico" aria-hidden="true" />
              {t('platform:billing.run')}
            </Button>
          ) : null
        }
      />

      {overview.isPending ? (
        <div className="stack" aria-busy="true">
          <Skeleton height={120} />
        </div>
      ) : overview.isError || !overview.data ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(overview.error, t)}
          traceId={errorTraceId(overview.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void overview.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : (
        <div className="stack">
          <div className="grid grid-4">
            <KpiCard
              label={t('platform:billing.kpi.mrr')}
              value={kpiValue(overview.data.mrr)}
              footer={kpiCount(overview.data.mrr, 'kpiSubscriptions')}
              tone="violet"
            />
            <KpiCard
              label={t('platform:billing.kpi.outstanding')}
              value={kpiValue(overview.data.outstanding)}
              footer={kpiCount(overview.data.outstanding, 'kpiInvoices')}
              tone="blue"
            />
            <KpiCard
              label={t('platform:billing.kpi.overdue')}
              value={kpiValue(overview.data.overdue)}
              footer={kpiCount(overview.data.overdue, 'kpiInvoices')}
              tone={overview.data.overdue.length > 0 ? 'red' : 'green'}
            />
            <KpiCard
              label={t('platform:billing.kpi.paid30')}
              value={kpiValue(overview.data.paidLast30Days)}
              footer={kpiCount(overview.data.paidLast30Days, 'kpiInvoices')}
              tone="green"
            />
          </div>

          <div className="grid-2">
            <Card>
              <CardHeader title={t('platform:billing.overdueTenants')} />
              <CardBody density="flush">
                {overview.data.overdueTenants.length === 0 ? (
                  <EmptyState title={t('platform:billing.noOverdue')} />
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:billing.columns.tenant')}</Th>
                          <Th align="right">{t('platform:billing.columns.balance')}</Th>
                          <Th>{t('platform:billing.columns.status')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.data.overdueTenants.map((row) => (
                          <Tr key={`${row.tenantId}-${row.currencyCode}`}>
                            <Td>
                              <div className="stack" style={{ gap: 2 }}>
                                <Link
                                  to="/platform/tenants/$tenantId"
                                  params={{ tenantId: row.tenantId }}
                                >
                                  {row.tenantName}
                                </Link>
                                <span className="t-12 muted">
                                  {t('platform:billing.oldestDue', {
                                    date: date(row.oldestDueDate),
                                  })}
                                  {row.suspensionScheduledAt
                                    ? ` · ${t('platform:billing.suspensionOn', { date: date(row.suspensionScheduledAt) })}`
                                    : ''}
                                </span>
                              </div>
                            </Td>
                            <Td numeric align="right">
                              {money(row.balance, row.currencyCode)}
                            </Td>
                            <Td>
                              <Badge tone={row.subscriptionStatus === 'PAST_DUE' ? 'red' : 'amber'}>
                                {t(`platform:subscriptionStatus.${row.subscriptionStatus}`)}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title={t('platform:billing.upcoming')} />
              <CardBody density="flush">
                {overview.data.upcoming.length === 0 ? (
                  <EmptyState title={t('platform:billing.noUpcoming')} />
                ) : (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('platform:billing.columns.tenant')}</Th>
                          <Th>{t('platform:billing.columns.issued')}</Th>
                          <Th align="right">{t('platform:billing.columns.total')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.data.upcoming.map((row) => (
                          <Tr key={`${row.tenantId}-${row.invoiceOn}`}>
                            <Td>
                              <div className="stack" style={{ gap: 2 }}>
                                <Link
                                  to="/platform/tenants/$tenantId"
                                  params={{ tenantId: row.tenantId }}
                                >
                                  {row.tenantName}
                                </Link>
                                <span className="t-12 muted">
                                  {row.planName} · {t(`platform:billing.upcomingKind.${row.kind}`)}
                                </span>
                              </div>
                            </Td>
                            <Td>{date(row.invoiceOn)}</Td>
                            <Td numeric align="right">
                              {money(row.amount, row.currencyCode)}
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader
              title={t('platform:billing.invoices')}
              icon={<Receipt className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody density="flush">
              <Toolbar>
                <div className="row row--wrap" style={{ gap: 8, flex: 1 }}>
                  {STATUSES.map((item) => (
                    <ChipFilter
                      key={item}
                      active={status === item}
                      onClick={() => setStatus((current) => (current === item ? undefined : item))}
                    >
                      {t(`platform:billing.status.${item}`)}
                    </ChipFilter>
                  ))}
                  <ChipFilter active={overdueOnly} onClick={() => setOverdueOnly((v) => !v)}>
                    {t('platform:billing.overdueOnly')}
                  </ChipFilter>
                </div>
              </Toolbar>
              {invoices.isPending ? (
                <TableSkeleton rows={5} columns={8} label={t('common:states.loading')} />
              ) : invoices.isError ? (
                <ErrorState
                  title={t('common:states.errorTitle')}
                  description={errorMessage(invoices.error, t)}
                  traceId={errorTraceId(invoices.error)}
                  retryLabel={t('common:actions.retry')}
                  onRetry={() => void invoices.refetch()}
                  traceCopyLabel={t('common:actions.copy')}
                  traceCopiedLabel={t('common:actions.copied')}
                />
              ) : rows.length === 0 ? (
                <EmptyState
                  icon={<Receipt className="ico" aria-hidden="true" />}
                  title={t('platform:billing.none')}
                  description={canManage ? t('platform:billing.noneHint') : undefined}
                />
              ) : (
                <>
                  <InvoiceTable invoices={rows} side="console" showTenant canManage={canManage} />
                  {invoices.hasNextPage ? (
                    <div className="row" style={{ justifyContent: 'center', padding: 12 }}>
                      <Button
                        variant="secondary"
                        disabled={invoices.isFetchingNextPage}
                        onClick={() => void invoices.fetchNextPage()}
                      >
                        {t('common:actions.loadMore')}
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </Container>
  );
}
